import clsx from "clsx";
import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Pie, PieChart, CartesianGrid, Cell, ComposedChart, Line, Legend, } from "recharts";
import { formatDateWithDateAndMonthName, FormatInvoiceDecimalNumber, getValueFromResource, setItemStatus } from "../../common/timeLiveFunction";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const TruncatedTick = (props: any) => {
  const { x, y, payload, angle, textAnchor } = props;
  const originalText = payload.value;
  const limit = angle === 0 ? 10 : 15;

  const truncatedText = originalText.length > limit ? originalText.slice(0, limit) + "..." : originalText;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={angle === 0 ? 15 : 10}
        textAnchor={textAnchor}
        fill="#6B7280"
        fontSize={11}
        fontWeight={500}
        transform={`rotate(${angle})`}
      >
        <title>{originalText}</title>
        {truncatedText}
      </text>
    </g>
  );
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];
const contentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
};

const formatLegendLabel = (value: string) => {
  if (!value || typeof value !== 'string') return String(value ?? '');
  return value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const DynamicWidget = ({ widget, widgetsResponseById, appliedFilters, isDraggable }: any) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredStatus, setHoveredStatus] = React.useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const widgetData = widgetsResponseById?.[widget.AccountEmployeeDashboardDetailId]?.data || [];

  const { cultureInfoName, decimalPlaces,OpenInNewTab } = useAppSelector((state) => state.globalState);

  const CustomColoredTooltip = ({ active, payload, currencyCode }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 12,
          color: '#1F2937',
          boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
          minWidth: '150px',
        }}
      >
        {payload.map((item: any, index: number) => {
          const color = item.payload?.fill || item.color || item.fill || '#888';
          const name = formatLegendLabel(String(item.name || item.dataKey || ''));
          const value = item.value;
          const dataKey = item.dataKey || '';
          const payloadName = item?.payload?.name || '';

          const forceTwoDecimal =
            dataKey === 'TimeOffHoursConsume' ||
            dataKey === 'TimeOffHoursAvailable' ||
            dataKey === 'TotalAmount' ||
            payloadName === 'BillableHours' ||
            payloadName === 'NonBillableHours';

          const resolvedDecimalPlaces = forceTwoDecimal ? 2 : currencyCode ? decimalPlaces : 0;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: index !== payload.length - 1 ? 6 : 0,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: color,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span>
                <strong style={{ color: '#111827' }}>{forceTwoDecimal ? getValueFromResource(`${name}`, dispatch, t) : name}</strong>
                {' : '}
                <strong>{currencyCode ? `${currencyCode} ` : ""} {FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, resolvedDecimalPlaces)}</strong>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const CustomBatteryTooltip = ({ active, payload, hoveredStatus, total }: any) => {
    if (!active || !payload || !payload.length || !hoveredStatus || !total)
      return null;

    const item = payload.find((p: any) => p.dataKey === hoveredStatus);
    if (!item) return null;

    const percentage = ((item.value / total) * 100).toFixed(1);

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 6,
          padding: "6px 10px",
          fontSize: 12,
          boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
          minWidth: 120,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: item.color,
            }}
          />
          <strong>{item.name}</strong>
        </div>
        <div style={{ marginTop: 4, color: "#374151" }}>
          {percentage}% <span style={{ color: "#9CA3AF" }}>({item.value})</span>
        </div>
      </div>
    );
  };

  const dataObj = widgetData?.[0];
  const value: any = dataObj ? Object.values(dataObj)[0] : 0;

  const noDataStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    textAlign: "center",
  };
  switch (widget.SystemDashboardWidgetTypeId) {
    case 1: // SummarizeCounter
      const isFeatureEnableds = widget?.IsFeatureEnabled !== false;

      const formattedData = widgetData?.slice(0, 3).map((item: any) => {
        return {
          label: item.Label,
          value: item.Value,
          ColumnName: item?.ColumnName,
          ColumnDescription: item?.ColumnDescription
        };
      });

      return (
        <div
          style={{
            ...contentStyle,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            cursor: isFeatureEnableds ? "pointer" : "not-allowed",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 10px",
            }}
          >
            {formattedData.map((item: any, index: number) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  textAlign: "center",
                  borderRight: index !== formattedData.length - 1 ? "2px solid #E5E7EB" : "none",
                  padding: "0 8px",
                }}
              >
                <div style={{ fontSize: 15, color: "#6B7280", marginBottom: 6 }}>
                  {getValueFromResource(`${item.label}`, dispatch, t)}
                </div>
                <div
                 data-tooltip-id="dashboard-tooltip"
                 data-tooltip-content={getValueFromResource(`${item.ColumnDescription}`, dispatch, t)}
                 data-tooltip-place="bottom"
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: (hoveredIndex === index && isFeatureEnableds) ? "#1675e0" : "#050b18",
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => {
                     if (isDraggable) return;
                    let status = null;

                    if (item.ColumnName === "OpenProjects" || item.ColumnName === "OverdueProjects" || item.ColumnName === "OpenTasks" || item.ColumnName === "OverdueTasks") {
                      const raw = widgetData?.[3];
                      const rawValue = raw?.Value ?? raw?.value;

                      if (rawValue) {
                        try {
                          status = JSON.parse(rawValue);
                        } catch (err) {
                          console.log("Invalid JSON:", rawValue);
                        }
                      }
                    }
                    if (widget?.IsDrillDown) {
                      let stateData: any = {
                        status: status,
                        fromDashboard: true
                      };

                      if (item.ColumnName === "OpenProjects" || item.ColumnName === "TotalProjects") {
                        stateData.widgetFilters = appliedFilters;
                      } else if (item.ColumnName === "OverdueProjects") {
                        stateData.widgetFilters = {
                          AccountPartyId: appliedFilters?.AccountPartyId,
                          AccountProjectTypeId: appliedFilters?.AccountProjectTypeId

                        };
                        stateData.IsOverdue = 1;
                      } else if (item.ColumnName === "OpenTasks" || item.ColumnName === "TotalTasks") {
                        stateData.widgetFilters = appliedFilters;
                      } else if (item.ColumnName === "OverdueTasks") {
                        stateData.widgetFilters = {
                          AccountTaskTypeId: appliedFilters?.AccountTaskTypeId,
                          AccountProjectId: appliedFilters?.AccountProjectId,
                        };
                        stateData.IsOverdue = 1;
                      }
                      const encoded = encodeURIComponent(JSON.stringify(stateData));

                      // window.open(`${widget?.DrillDownRoute}?data=${encoded}`, "_blank");                      
                      const targetUrl = `${widget?.DrillDownRoute}?data=${encoded}`;
                      if (OpenInNewTab) {
                        window.open(targetUrl, "_blank");
                      } else {
                        localStorage.setItem('returnToDashboard', window.location.pathname + window.location.search);
                        navigate(targetUrl);
                      }
                    }
                  }}
                >
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 2: // Counter
      const isFeatureEnabled = widget?.IsFeatureEnabled !== false;
      return (
        <div
          style={{
            ...contentStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 25,
            fontWeight: 700,
            color: (isHovered && isFeatureEnabled) ? "#1675e0" : "#050b18",
            transition: "color 0.2s ease",
            cursor: isFeatureEnabled ? "pointer" : "not-allowed",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <small
            onClick={() => {
               if (isDraggable) return;
              if (!isFeatureEnabled) return;
              let IsOverDue = widget?.WidgetName === 'Overdue Tasks' || widget?.WidgetName === 'Overdue Projects'
              let status = null;
              if (dataObj?.Status) {
                try {
                  status = JSON.parse(dataObj.Status);
                } catch (e) {
                  console.log("Invalid JSON:", dataObj.Status);
                }
              }

              if (widget?.IsDrillDown) {
                const stateData = {
                  status,
                  widgetFilters: appliedFilters,
                  fromDashboard: true,
                  IsOverdue: IsOverDue ? 1 : 0
                };

                const encoded = encodeURIComponent(JSON.stringify(stateData));
                const targetUrl = `${widget?.DrillDownRoute}?data=${encoded}`;
                 if (OpenInNewTab) {
                        window.open(targetUrl, "_blank");
                      } else {
                        localStorage.setItem('returnToDashboard', window.location.pathname + window.location.search);
                        navigate(targetUrl);
                      }
                // window.open(`${widget?.DrillDownRoute}?data=${encoded}`, "_blank");
              }
            }}
          >
            {dataObj?.Counter === 'ExpenseTotal' ? `${dataObj?.CurrencyCode} ${FormatInvoiceDecimalNumber(cultureInfoName.toString(), dataObj?.TotalAmount ?? 0, decimalPlaces)}` : dataObj?.TotalHours ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), dataObj?.TotalHours ?? 0, 2) : value}
          </small>
        </div>
      );

    case 3: // Battery
      const batteryData = widgetData || [];
      if (!batteryData.length) return <div style={noDataStyle}>
        <p className='noDataFound'>
          {getValueFromResource('No Record Found', dispatch, t)}

        </p>
      </div>;

      const keys = Object.keys(batteryData[0]);
      const labelKey = keys[0];
      const valueKey = keys[1];

      const statusKeys = batteryData.map((item: any) => item[labelKey]);

      const singleRowData = [
        batteryData.reduce((acc: any, curr: any) => {
          acc[curr[labelKey]] = curr[valueKey];
          return acc;
        }, {}),
      ];

      const total = batteryData.reduce(
        (sum: number, item: any) => sum + Number(item[valueKey]),
        0
      );

      const batteryColors = [
        "#2563EB",
        "#22c55e",
        "#eab308",
        "#ef4444",
        "#a855f7",
        "#ec4899",
      ];



      return (
        <div
          style={{
            ...contentStyle,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "100%", height: 35 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={singleRowData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis type="number" hide domain={[0, total]} />
                <YAxis type="category" hide />
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  content={
                    <CustomBatteryTooltip
                      hoveredStatus={hoveredStatus}
                      total={total}
                    />
                  }
                />
                {statusKeys.map((status: string, index: number) => {
                  let radius: any = [0, 0, 0, 0];
                  if (statusKeys.length === 1) radius = [8, 8, 8, 8];
                  else if (index === 0) radius = [8, 0, 0, 8];
                  else if (index === statusKeys.length - 1) radius = [0, 8, 8, 0];

                  return (
                    <Bar
                      key={status}
                      dataKey={status}
                      stackId="a"
                      fill={batteryColors[index % batteryColors.length]}
                      barSize={48}
                      radius={radius}
                      onMouseEnter={() => setHoveredStatus(status)}
                      onMouseLeave={() => setHoveredStatus(null)}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>


          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              fontSize: "12px",
              marginTop: 10,
            }}
          >
            {statusKeys.map((status: string, index: number) => (
              <div
                key={status}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: batteryColors[index % batteryColors.length],
                  }}
                />
                <span style={{ color: "#6B7280", whiteSpace: "nowrap" }}>
                  {status}:{" "}
                  <strong style={{ color: "#111827" }}>
                    {singleRowData[0][status]}
                  </strong>
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    case 4: // Tabular
      const tableData = widgetData;

      const hasData = Array.isArray(tableData) && tableData.length > 0 && tableData.some((row: any) => row && Object.keys(row).length > 0);
      const hourKeySet = new Set(['Planned Hours', 'Actual Hours', 'Hours', 'BillableHours', 'NonBillableHours', 'Duration', 'Days', 'Total Hours', 'Available']);
      return (
        <div style={{ overflowY: "auto", maxHeight: 250 }}>
          {hasData ? (
            <table className="table table-row-bordered align-middle gs-1 gy-1 gx-1">
              <thead style={{ position: "sticky", top: 0, zIndex: 1, background: '#fff' }}>
                <tr className="thHeading align-middle">
                  {Object.keys(tableData[0] || {}).map((key) => (
                    <th className="w-25px" key={key}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row: any, idx: number) => {
                  if (!row || typeof row !== "object") return null;

                  return (
                    <tr className="thWidget align-middle" key={idx}>
                      {Object.entries(row).map(([key, val]: any, i: number) => {
                        let parsedVal = val;
                        if (key === "TimesheetPeriod" && typeof val === "string") {
                          try {
                            parsedVal = JSON.parse(val.replace(/'/g, '"'));
                          } catch {
                            parsedVal = val;
                          }
                        }
                        const cleanKey = key?.trim();
                        const isHourKey = hourKeySet.has(cleanKey);
                        const isCurrencyKey = !isHourKey && (cleanKey?.startsWith("Amount (") || cleanKey?.startsWith("Billable (") || cleanKey?.startsWith("NonBillable ("));

                        return (
                          <td key={`${idx}-${i}`}>
                            {key === "Status" ? (
                              <span
                                className={clsx("timeOffStatus-td app-navbar-item badge", setItemStatus(val))}
                              >
                                {val}
                              </span>
                            ) : (key === "Deadline Date" || key === 'Attendance Date' || key === 'StartDate' || key === 'EndDate' || key === 'TimeEntryStartDate' || key === 'TimeEntryEndDate' || key === 'SheetDate') ? (
                              <span>
                                {formatDateWithDateAndMonthName(val, cultureInfoName.toString())}
                              </span>
                            ) : key === "TimesheetPeriod" ? (
                              <span>
                                {formatDateWithDateAndMonthName(parsedVal[0], cultureInfoName.toString())} - {formatDateWithDateAndMonthName(parsedVal[1], cultureInfoName.toString())}
                              </span>
                            ) : (typeof val === "number" && isHourKey) ? (
                              FormatInvoiceDecimalNumber(cultureInfoName.toString(), val, 2)

                            ) : (typeof val === "string" && isCurrencyKey) ? (
                              FormatInvoiceDecimalNumber(cultureInfoName.toString(), Number(val), decimalPlaces)

                            ) : (
                              val
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={noDataStyle}>
              <p className='noDataFound'>
                {getValueFromResource('No Record Found', dispatch, t)}
              </p>
            </div>
          )
          }
        </div >
      );

    case 5: {
      const pieDataRaw = widgetData?.[0] || {};
      const pieKeys = Object.keys(pieDataRaw);
      if (!pieKeys.length) return <div style={noDataStyle}> <p className='noDataFound'>
        {getValueFromResource('No Record Found', dispatch, t)}

      </p></div>;

      const totalPieValue = pieKeys.reduce((sum, key) => sum + Number(pieDataRaw[key]), 0);

      const pieChartData = pieKeys.map((key) => ({
        name: key,
        value: pieDataRaw[key],
        total: totalPieValue,
      }));

      const pieColors = ["#4266b3", "#333849", "#FFBB28", "#FF8042"];

      return (
        <div style={{ ...contentStyle, flexDirection: "column" }}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={65}
                paddingAngle={2}
              >
                {pieChartData.map((_, index) => (
                  <Cell key={index} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomColoredTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 16, fontSize: 12, flexWrap: "wrap" }}>
            {pieChartData.map((item, index) => {
              const percent = totalPieValue ? ((item.value / totalPieValue) * 100).toFixed(1) : 0;
              return (
                <div key={item.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: pieColors[index % pieColors.length] }} />
                  <span>{getValueFromResource(`${item.name}`, dispatch, t)}: <b>{FormatInvoiceDecimalNumber(cultureInfoName.toString(), item.value, 2)}</b> ({percent}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    case 6: // Pie-Donut Chart
      if (!widgetData.length) return <div style={noDataStyle}> <p className='noDataFound'>
        {getValueFromResource('No Record Found', dispatch, t)}

      </p></div>;

      const donutData = widgetData.map((item: any) => ({
        name: item[Object.keys(item)[0]],
        value: item[Object.keys(item)[1]],
      }));

      const totalDonut = donutData.reduce(
        (sum: any, item: any) => sum + Number(item.value),
        0
      );
      const donutDataWithTotal = donutData.map((item: any) => ({ ...item, total: totalDonut }));
      const donutColors = ["#f84134", "#1c9ff7", "#dda924", "#24c5c5"];

      return (
        <div style={{ ...contentStyle, flexDirection: "column" }}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                // data={donutData}
                data={donutDataWithTotal}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={0}
                paddingAngle={2}
              >
                {donutDataWithTotal.map((_: any, index: any) => (
                  <Cell key={index} fill={donutColors[index % donutColors.length]} />
                ))}
              </Pie>

              <Tooltip content={<CustomColoredTooltip />} />
            </PieChart>
          </ResponsiveContainer>


          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "center",
              gap: 16,
              fontSize: 12,
              flexWrap: "wrap",
            }}
          >
            {donutData.map((item: any, index: any) => {
              const percent = totalDonut
                ? ((item.value / totalDonut) * 100).toFixed(1)
                : 0;
              return (
                <div
                  key={item.name}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: donutColors[index % donutColors.length],
                    }}
                  />
                  <span>
                    {item.name}: <b>{item.value}</b> ({percent}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );

    case 7: // Line

      return <div>Line Widget</div>;
    // Bar
    case 8: {
  const widgetData = widgetsResponseById?.[widget.AccountEmployeeDashboardDetailId]?.data || [];

  if (!widgetData.length) {
    return (
      <div style={noDataStyle}>
        <p className='noDataFound'>
          {getValueFromResource('No Record Found', dispatch, t)}
        </p>
      </div>
    );
  }

  const allKeys = Object.keys(widgetData[0] || {});

  if (!allKeys.length) {
    return (
      <div style={noDataStyle}>
        <p className='noDataFound'>
          {getValueFromResource('No Record Found', dispatch, t)}
        </p>
      </div>
    );
  }

  const isVerticalLayout = widget.SystemDashboardWidgetId === 7;

  const labelKey =
    allKeys.find(k => {
      const val = widgetData[0][k];

      if (typeof val === "string" && val.length > 8) return false;
      if (/id/i.test(k)) return false;
      if (/currency/i.test(k)) return false;

      return typeof val === "string";
    }) || allKeys[0];

  let numericKeys = allKeys.filter(
    k => k !== labelKey && typeof widgetData[0][k] === "number"
  );

  const originalData = widgetData.map((item: any) => {
    const obj: any = { [labelKey]: item[labelKey] };

    numericKeys.forEach(k => {
      obj[k] = item[k];
    });

    return obj;
  });

  let maxVal = -Infinity;
  let minPositive = Infinity;

  originalData.forEach((item: any) => {
    numericKeys.forEach(k => {
      const v = item[k];

      if (typeof v === "number" && !isNaN(v)) {
        if (v > maxVal) maxVal = v;
        if (v > 0 && v < minPositive) minPositive = v;
      }
    });
  });

  const useTransformation = false;

  const chartData = originalData.map((item: any) => {
    const newItem: any = {
      [labelKey]: item[labelKey]
    };

    numericKeys.forEach(k => {
      const original = item[k];

      if (typeof original === "number" && !isNaN(original)) {
        if (useTransformation) {
          newItem[k] = Math.log1p(original);
          newItem[`${k}_original`] = original;
        } else {
          newItem[k] = original;
        }
      } else {
        newItem[k] = 0;
        newItem[`${k}_original`] = 0;
      }
    });

    return newItem;
  });

  // SORT FOR VERTICAL CHART
  const sortedChartData = [...chartData].sort((a: any, b: any) => {
  const totalA = numericKeys.reduce(
    (sum, key) => sum + (a[key] || 0),
    0
  );

  const totalB = numericKeys.reduce(
    (sum, key) => sum + (b[key] || 0),
    0
  );

  return totalB ;
});

  const colors = ["#3b82f6", "#1f2937", "#22c55e", "#f97316"];
  const currencyCode = widgetData?.[0]?.CurrencyCode;

  // const TransformedTooltip = ({ active, payload }: any) => {
  //   if (!active || !payload || !payload.length) return null;

  //   return (
  //     <div
  //       style={{
  //         background: "#fff",
  //         border: "1px solid #E5E7EB",
  //         borderRadius: 8,
  //         padding: "8px 12px",
  //         fontSize: 12,
  //         color: "#1F2937",
  //         boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
  //         minWidth: "150px"
  //       }}
  //     >
  //       {payload.map((entry: any, idx: number) => {
  //         const originalValue =
  //           entry.payload[`${entry.dataKey}_original`] ?? entry.value;

  //         const name = formatLegendLabel(
  //           String(entry.name || entry.dataKey)
  //         );

  //         const shouldForceTwoDecimals =
  //           name === "TotalAmount" || name === "Amount";

  //         const resolvedDecimalPlaces = shouldForceTwoDecimals
  //           ? 2
  //           : currencyCode
  //           ? decimalPlaces
  //           : 0;

  //         return (
  //           <div
  //             key={idx}
  //             style={{
  //               display: "flex",
  //               alignItems: "center",
  //               gap: 8,
  //               marginBottom:
  //                 idx !== payload.length - 1 ? 6 : 0
  //             }}
  //           >
  //             <span
  //               style={{
  //                 width: 10,
  //                 height: 10,
  //                 borderRadius: "50%",
  //                 backgroundColor: entry.color,
  //                 display: "inline-block",
  //                 flexShrink: 0
  //               }}
  //             />

  //             <span>
  //               <strong style={{ color: "#111827" }}>
  //                 {getValueFromResource(name, dispatch, t)}
  //               </strong>

  //               {" : "}

  //               <strong>
  //                 {currencyCode ? `${currencyCode} ` : ""}
  //                 {FormatInvoiceDecimalNumber(
  //                   cultureInfoName.toString(),
  //                   originalValue,
  //                   resolvedDecimalPlaces
  //                 )}
  //               </strong>
  //             </span>
  //           </div>
  //         );
  //       })}
  //     </div>
  //   );
  // };

  const TransformedTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;
        return (
          <div
            style={{
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              color: '#1F2937',
              boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
              minWidth: '150px',
            }}
          >
            {payload.map((entry: any, idx: number) => {
              const originalValue = entry.payload[`${entry.dataKey}_original`] ?? entry.value;
              const name = formatLegendLabel(String(entry.name || entry.dataKey));
              const shouldForceTwoDecimals = name === 'TotalAmount' || name === 'Amount';
              const resolvedDecimalPlaces = shouldForceTwoDecimals ? 2 : (currencyCode ? decimalPlaces : 0);
              const isNegative = originalValue < 0; 
              const absValue = Math.abs(originalValue); 
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: idx !== payload.length - 1 ? 6 : 0 }}>

                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: isNegative ? "#ef4444" : entry.color, display: 'inline-block', flexShrink: 0 }} />
                  <span>

                    <strong style={{ color: isNegative ? "#ef4444" : "#111827" }}>{isNegative ? `Exceeded by ${absValue} Hours` : getValueFromResource(name, dispatch, t)}</strong>
                 
                  {!isNegative && (
                      <>
                        {' : '}
                        <strong>
                          {currencyCode ? `${currencyCode} ` : ''}
                          {FormatInvoiceDecimalNumber(cultureInfoName.toString(), originalValue, resolvedDecimalPlaces)}
                        </strong>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        );
      };
  return (
    <div style={{ ...contentStyle, width: "100%", height: "100%" }}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={isVerticalLayout ? sortedChartData : chartData}
          layout={isVerticalLayout ? "vertical" : "horizontal"}
          margin={{
            top: 20,
            right: 30,
            left: isVerticalLayout ? 0 : -10,
            bottom: 10
          }}
          barGap={8}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#F3F4F6"
          />

          {isVerticalLayout ? (
            <>
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "#9CA3AF",
                  fontSize: 11
                }}
              />

              <YAxis
                type="category"
                dataKey={labelKey}
                axisLine={false}
                tickLine={false}
                width={100}
                tick={{
                  fill: "#374151",
                  fontSize: 11
                }}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={labelKey}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={originalData.length > 5 ? -45 : 0}
                textAnchor={
                  originalData.length > 5
                    ? "end"
                    : "middle"
                }
                tick={(tickProps) => (
                  <TruncatedTick
                    {...tickProps}
                    angle={
                      originalData.length > 5 ? -45 : 0
                    }
                    textAnchor={
                      originalData.length > 5
                        ? "end"
                        : "middle"
                    }
                  />
                )}
                height={63}
                dy={20}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                domain={[0, "auto"]}
                tick={{
                  fill: "#9CA3AF",
                  fontSize: 11
                }}
              />
            </>
          )}

          <Tooltip content={<TransformedTooltip />} />

          <Legend
            verticalAlign="top"
            align="right"
            content={() => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 10
                }}
              >
                {numericKeys.map((key, index) => {
                  const total = originalData.reduce(
                    (sum: number, item: any) => {
                      return sum + (item[key] || 0);
                    },
                    0
                  );

                  return (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            colors[index % colors.length],
                          display: "inline-block"
                        }}
                      />

                      <span
                        style={{
                          color: "#374151",
                          fontWeight: 500
                        }}
                      >
                        {getValueFromResource(
                          formatLegendLabel(key),
                          dispatch,
                          t
                        )}{" "}
                        <strong>
                          {currencyCode
                            ? `${currencyCode} `
                            : ""}
                          {currencyCode
                            ? FormatInvoiceDecimalNumber(
                                cultureInfoName.toString(),
                                total,
                                decimalPlaces
                              )
                            : total}
                        </strong>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          />

          {numericKeys.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              name={k}
              barSize={
                originalData.length > 4 ? 20 : 40
              }
              radius={
                isVerticalLayout
                  ? [0, 6, 6, 0]
                  : [6, 6, 0, 0]
              }
              fill={colors[i % colors.length]}
              activeBar={{ fillOpacity: 0.8 }}
            >
              {(isVerticalLayout
                ? sortedChartData
                : chartData
              ).map((entry: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry[k] === 0 ? "transparent" : entry[k] < 0 ? "#ef4444" : colors[i % colors.length]}
                  // fill={
                  //   entry[k] === 0
                  //     ? "transparent"
                  //     : colors[i % colors.length]
                  // }
                />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
    // case 8: {
    //   const widgetData = widgetsResponseById?.[widget.AccountEmployeeDashboardDetailId]?.data || [];
    //   if (!widgetData.length) {
    //     return (
    //       <div style={noDataStyle}>
    //         <p className='noDataFound'>{getValueFromResource('No Record Found', dispatch, t)}</p>
    //       </div>
    //     );
    //   }

    //   const allKeys = Object.keys(widgetData[0] || {});
    //   if (!allKeys.length) {
    //     return (
    //       <div style={noDataStyle}>
    //         <p className='noDataFound'>{getValueFromResource('No Record Found', dispatch, t)}</p>
    //       </div>
    //     );
    //   }

    //   const labelKey = allKeys.find(k => {
    //     const val = widgetData[0][k];
    //     if (typeof val === "string" && val.length > 8) return false;
    //     if (/id/i.test(k)) return false;
    //     if (/currency/i.test(k)) return false;
    //     return typeof val === "string";
    //   }) || allKeys[0];

    //   let numericKeys = allKeys.filter(k => k !== labelKey && typeof widgetData[0][k] === "number");
    //   const originalData = widgetData.map((item: any) => {
    //     const obj: any = { [labelKey]: item[labelKey] };
    //     numericKeys.forEach(k => (obj[k] = item[k]));
    //     return obj;
    //   });

    //   let maxVal = -Infinity;
    //   let minPositive = Infinity;
    //   originalData.forEach((item: any) => {
    //     numericKeys.forEach(k => {
    //       const v = item[k];
    //       if (typeof v === 'number' && !isNaN(v)) {
    //         if (v > maxVal) maxVal = v;
    //         if (v > 0 && v < minPositive) minPositive = v;
    //       }
    //     });
    //   });
    //    //const useTransformation = minPositive !== Infinity && maxVal / minPositive > 100;
    //   const useTransformation = false;
      

    //   const chartData = originalData.map((item: any) => {
    //     const newItem: any = { [labelKey]: item[labelKey] };
    //     numericKeys.forEach(k => {
    //       const original = item[k];
    //       if (typeof original === 'number' && !isNaN(original)) {
    //         if (useTransformation) {
    //           newItem[k] = Math.log1p(original);
    //           newItem[`${k}_original`] = original;
    //         } else {
    //           newItem[k] = original;
    //         }
    //       } else {
    //         newItem[k] = 0;
    //         newItem[`${k}_original`] = 0;
    //       }
    //     });
    //     return newItem;
    //   });

    //   const toTransformed = (val: number) => Math.log1p(val);
    //   const toOriginal = (transformed: number) => Math.expm1(transformed);

    //   const getYAxisTicks = () => {
    //     if (!useTransformation) return undefined;

    //     const maxOriginal = maxVal;
    //     const candidates: number[] = [0];

    //     let power = 10;

    //     while (power <= maxOriginal) {
    //       candidates.push(power);
    //       power *= 10;
    //     }

    //     if (candidates.indexOf(maxOriginal) === -1) {
    //       candidates.push(maxOriginal);
    //     }

    //     const unique: number[] = [];
    //     candidates.forEach((value) => {
    //       if (unique.indexOf(value) === -1) {
    //         unique.push(value);
    //       }
    //     });

    //     unique.sort((a, b) => a - b);

    //     return unique.map((v) => toTransformed(v));
    //   };

    //   const colors = ["#3b82f6", "#1f2937", "#22c55e", "#f97316"];
    //   const currencyCode = widgetData?.[0]?.CurrencyCode;

    //   const TransformedTooltip = ({ active, payload }: any) => {
    //     if (!active || !payload || !payload.length) return null;
    //     return (
    //       <div
    //         style={{
    //           background: '#fff',
    //           border: '1px solid #E5E7EB',
    //           borderRadius: 8,
    //           padding: '8px 12px',
    //           fontSize: 12,
    //           color: '#1F2937',
    //           boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
    //           minWidth: '150px',
    //         }}
    //       >
    //         {payload.map((entry: any, idx: number) => {
    //           const originalValue = entry.payload[`${entry.dataKey}_original`] ?? entry.value;
    //           const name = formatLegendLabel(String(entry.name || entry.dataKey));
    //           const shouldForceTwoDecimals = name === 'TotalAmount' || name === 'Amount';
    //           const resolvedDecimalPlaces = shouldForceTwoDecimals ? 2 : (currencyCode ? decimalPlaces : 0);
    //           return (
    //             <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: idx !== payload.length - 1 ? 6 : 0 }}>
    //               <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, display: 'inline-block', flexShrink: 0 }} />
    //               <span>
    //                 <strong style={{ color: '#111827' }}>{getValueFromResource(name, dispatch, t)}</strong>
    //                 {' : '}
    //                 <strong>
    //                   {currencyCode ? `${currencyCode} ` : ''}
    //                   {FormatInvoiceDecimalNumber(cultureInfoName.toString(), originalValue, resolvedDecimalPlaces)}
    //                 </strong>
    //               </span>
    //             </div>
    //           );
    //         })}
    //       </div>
    //     );
    //   };

    //   return (
    //     <div style={{ ...contentStyle, width: "100%", height: "100%" }}>
    //       <ResponsiveContainer width="100%" height={280}>
    //         <BarChart
    //           data={chartData}
    //           margin={{ top: 20, right: 30, left: -10, bottom: 10 }}
    //           barGap={8}
    //         >
    //           <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />

    //           <XAxis
    //             dataKey={labelKey}
    //             axisLine={false}
    //             tickLine={false}
    //             interval={0}
    //             angle={originalData.length > 5 ? -45 : 0}
    //             textAnchor={originalData.length > 5 ? "end" : "middle"}
    //             tick={(tickProps) => (
    //               <TruncatedTick
    //                 {...tickProps}
    //                 angle={originalData.length > 5 ? -45 : 0}
    //                 textAnchor={originalData.length > 5 ? "end" : "middle"}
    //               />
    //             )}
    //             height={63}
    //             dy={20}
    //           />

    //           <YAxis
    //             axisLine={false}
    //             tickLine={false}
    //             domain={useTransformation ? [0, 'auto'] : [0, 'auto']}
    //             ticks={getYAxisTicks()}
    //             tickFormatter={(tickValue) => {
    //               if (!useTransformation) return tickValue >= 1000 ? `${(tickValue / 1000).toFixed(1)}k` : tickValue;
    //               const original = toOriginal(tickValue);
    //               if (original === 0) return '0';
    //               if (original >= 1000000) return `${(original / 1000000).toFixed(1)}M`;
    //               if (original >= 1000) return `${(original / 1000).toFixed(1)}k`;
    //               return Math.round(original).toString();
    //             }}
    //             tick={{ fill: "#9CA3AF", fontSize: 11 }}
    //           />

    //           <Tooltip content={<TransformedTooltip />} />
    //           <Legend
    //             verticalAlign="top"
    //             align="right"
    //             content={() => (
    //               <div
    //                 style={{
    //                   display: "flex",
    //                   justifyContent: "flex-end",
    //                   gap: 16,
    //                   flexWrap: "wrap",
    //                   marginBottom: 10
    //                 }}
    //               >
    //                 {numericKeys.map((key, index) => {
    //                   const total = originalData.reduce((sum: number, item: any) => {
    //                     return sum + (item[key] || 0);
    //                   }, 0);

    //                   return (
    //                     <div
    //                       key={key}
    //                       style={{
    //                         display: "flex",
    //                         alignItems: "center",
    //                         gap: 6
    //                       }}
    //                     >
    //                       <span
    //                         style={{
    //                           width: 8,
    //                           height: 8,
    //                           borderRadius: "50%",
    //                           backgroundColor: colors[index % colors.length],
    //                           display: "inline-block"
    //                         }}
    //                       />

    //                       <span style={{ color: "#374151", fontWeight: 500 }}>
    //                         {getValueFromResource(formatLegendLabel(key), dispatch, t)}{" "}
    //                         <strong>
    //                           {currencyCode ? `${currencyCode} ` : ""} {currencyCode ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), total, decimalPlaces) : total}
    //                         </strong>
    //                       </span>
    //                     </div>
    //                   );
    //                 })}
    //               </div>
    //             )}
    //           />

    //           {numericKeys.map((k, i) => (
    //             <Bar
    //               key={k}
    //               dataKey={k}
    //               name={k}
    //               barSize={originalData.length > 4 ? 20 : 40}
    //               radius={[6, 6, 0, 0]}
    //               fill={colors[i % colors.length]}
    //               activeBar={{ fillOpacity: 0.8 }}
    //             >
    //               {chartData.map((entry: any, index: number) => (
    //                 <Cell
    //                   key={`cell-${index}`}
    //                   fill={entry[k] === 0 ? "transparent" : colors[i % colors.length]}
    //                 />
    //               ))}
    //             </Bar>
    //           ))}
    //         </BarChart>
    //       </ResponsiveContainer>
    //     </div>
    //   );
    // }

    default:
      return <div style={noDataStyle}>
        <p className='noDataFound'>
          {getValueFromResource('No Record Found', dispatch, t)}
        </p>
      </div>;
  }
};

export default DynamicWidget;